/* eslint-disable react-hooks/exhaustive-deps */
// DealContext.js
import { createContext, useContext, useState, useEffect } from "react";
import { getUrl, uploadData } from "aws-amplify/storage";
import { getCurrentUser, fetchAuthSession } from "aws-amplify/auth";
import { useParams, useNavigate } from "react-router-dom";

// Utility imports
import { calculateGroupData } from "../utils/groupDataCalculator";
import { validateAndNormalizeData } from "../utils/dataValidator";
import { createConsolidatedData } from "../utils/dataConsolidator";

// Helper function to sort periods in the correct order:
// 1. Years (2024, 2025, etc.) in ascending order
// 2. Trailing periods (T12, T6, T3, T1) in descending order
// 3. Projections (Projection 1, 2, 3) in ascending order
export const sortDealPeriods = (periods) => {
  if (!periods || periods.length === 0) return periods;

  return [...periods].sort((a, b) => {
    // Check if period is a year (e.g., "2024")
    const isYearA = /^\d{4}$/.test(a);
    const isYearB = /^\d{4}$/.test(b);

    // Check if period is a trailing period (e.g., "T12 June 2025" or "T12")
    const trailingMatchA = a.match(/^T(\d+)/);
    const trailingMatchB = b.match(/^T(\d+)/);

    // Check if period is a projection (e.g., "Projection 1")
    const projectionMatchA = a.match(/Projection\s*(\d+)/i);
    const projectionMatchB = b.match(/Projection\s*(\d+)/i);

    // Both are years - sort in ascending order
    if (isYearA && isYearB) {
      return parseInt(a) - parseInt(b);
    }

    // Both are trailing periods - sort in descending order (T12 > T6 > T3 > T1)
    if (trailingMatchA && trailingMatchB) {
      const numA = parseInt(trailingMatchA[1]);
      const numB = parseInt(trailingMatchB[1]);
      return numB - numA; // Descending order
    }

    // Both are projections - sort in ascending order
    if (projectionMatchA && projectionMatchB) {
      const numA = parseInt(projectionMatchA[1]);
      const numB = parseInt(projectionMatchB[1]);
      return numA - numB; // Ascending order
    }

    // Mixed types - apply priority: Years first, then Trailing, then Projections
    if (isYearA) return -1;
    if (isYearB) return 1;

    if (trailingMatchA) return -1;
    if (trailingMatchB) return 1;

    if (projectionMatchA) return -1;
    if (projectionMatchB) return 1;

    // Default: maintain original order
    return 0;
  });
};

// Helper function to find the default period
// Prefers T3, then the closest trailing period to T3 (T1, T6, T12, etc.)
export const findDefaultPeriod = (periods) => {
  if (!periods || periods.length === 0) return null;

  // First, try to find T3
  const t3Period = periods.find((p) => p.match(/^T3\b/));
  if (t3Period) return t3Period;

  // If no T3, find the closest trailing period to 3
  const trailingPeriods = periods
    .map((p) => {
      const match = p.match(/^T(\d+)/);
      return match ? { period: p, number: parseInt(match[1]) } : null;
    })
    .filter(Boolean);

  if (trailingPeriods.length > 0) {
    // Find the trailing period with number closest to 3
    const closest = trailingPeriods.reduce((prev, curr) => {
      const prevDiff = Math.abs(prev.number - 3);
      const currDiff = Math.abs(curr.number - 3);
      return currDiff < prevDiff ? curr : prev;
    });
    return closest.period;
  }

  // Fallback to first period if no trailing periods exist
  return periods[0];
};

export const DealContext = createContext();

export const DealProvider = ({ children }) => {
  // State declarations
  const [consolidatedData, setConsolidatedData] = useState(null);
  const [dataInconsistencies, setDataInconsistencies] = useState([]);
  const [currentDeal, setCurrentDeal] = useState(null);
  const [currentDealName, setCurrentDealName] = useState(null);
  const [currentFacility, setCurrentFacility] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [portfolioFacilities, setPortfolioFacilities] = useState(null);
  const [facilityGroups, setFacilityGroups] = useState([]);
  const [allFacilityGroups, setAllFacilityGroups] = useState([]);
  const [currentGroup, setCurrentGroup] = useState(null);
  const [competitionData, setCompetitionData] = useState(null);
  const [currentCompetitionData, setCurrentCompetitionData] = useState(null);
  const [archivedCompetitionData, setArchivedCompetitionData] = useState(null);
  const { dealName: urlDealName } = useParams();
  const [hasHandledInitialUrl, setHasHandledInitialUrl] = useState(false);
  const [isDealLoading, setIsDealLoading] = useState(false);
  const [hasArchiveAccess, setHasArchiveAccess] = useState(false);
  const [viewingArchived, setViewingArchived] = useState(() => {
    const savedState = sessionStorage.getItem("viewingArchived");
    return savedState === "true";
  });
  const [currentDealsData, setCurrentDealsData] = useState(null);
  const [archivedDealsData, setArchivedDealsData] = useState(null);
  const [hasCurrentDealsData, setHasCurrentDealsData] = useState(false);
  const [hasArchivedDealsData, setHasArchivedDealsData] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        setIsLoading(true);
        // First check if we have a current user
        const user = await getCurrentUser();
        // Then verify we have a valid session
        const session = await fetchAuthSession();

        if (session?.tokens?.accessToken?.toString()) {
          setIsAuthenticated(true);
          setAuthError(null);

          // Check for archive access permission
          try {
            const idToken = session.tokens?.idToken;
            const payload = idToken?.payload;

            let noAccess = false;

            if (payload && payload["cognito:groups"]) {
              const userGroups = payload["cognito:groups"];
              noAccess = userGroups.includes("noArchiveAccess");
            }

            setHasArchiveAccess(!noAccess);

            // If user has no access but is in archived view, reset to current
            if (
              noAccess &&
              sessionStorage.getItem("viewingArchived") === "true"
            ) {
              console.log(
                "User doesn't have archive access. Resetting to current view."
              );
              setViewingArchived(false);
              sessionStorage.setItem("viewingArchived", "false");
            }
          } catch (error) {
            console.error("Error checking archive permissions:", error);
            setHasArchiveAccess(false); // Default to no access on error
          }
        } else {
          throw new Error("No valid session found");
        }
      } catch (error) {
        setAuthError(error);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Main fetch function to get all data
  const fetchData = async () => {
    try {
      setIsLoading(true);

      // Always fetch current data first
      const currentData = await fetchDealsData("current");

      // Process current deals data
      if (
        Object.keys(currentData.allInOne).length === 0 ||
        Object.keys(currentData.facilities).length === 0
      ) {
        console.error("❌ Failed to load critical current deals data");
        alert(
          "Critical data failed to load. Please check your connection and try again."
        );
        // Set empty data to prevent crashes
        setCurrentDealsData({});
        setHasCurrentDealsData(false);
        setIsLoading(false);
        return;
      }

      // Update portfolio facilities for current data
      setPortfolioFacilities(currentData.portfolioFacilities);

      // Set current competition data
      setCurrentCompetitionData(currentData.competitionOverview);
      // Set active competition data based on viewing state
      if (!viewingArchived) {
        setCompetitionData(currentData.competitionOverview);
      }

      // Process and normalize the current data
      const { inconsistencies, dealMap } = validateAndNormalizeData(
        currentData.allInOne,
        currentData.facilities,
        currentData.hudDebt
      );

      // Set data inconsistencies
      setDataInconsistencies(inconsistencies);

      // Create consolidated data
      const currentConsolidated = createConsolidatedData(
        currentData.allInOne,
        currentData.facilities,
        currentData.hudDebt,
        dealMap
      );

      setCurrentDealsData(currentConsolidated);
      setHasCurrentDealsData(true);

      // Set consolidated data for current view immediately
      if (!viewingArchived) {
        setConsolidatedData(currentConsolidated);
        setCompetitionData(currentData.competitionOverview);
      }

      // Handle URL-based deal selection for current deals if we have a URL
      if (urlDealName && !hasHandledInitialUrl && !viewingArchived) {
        if (currentConsolidated && currentConsolidated[urlDealName]) {
          const deal = currentConsolidated[urlDealName];
          // Set all states in one go to avoid race conditions
          setCurrentDeal(deal);
          setCurrentDealName(urlDealName);
          setCurrentGroup(null);

          // Handle consolidated facility selection
          const consolidatedFacility = deal.facilities["Consolidated"];
          if (consolidatedFacility) {
            setCurrentFacility(consolidatedFacility);
            const periods = Object.keys(consolidatedFacility.periods || {});
            const defaultPeriod = findDefaultPeriod(periods);
            if (defaultPeriod) {
              setSelectedPeriod(defaultPeriod);
            }
          }
          setHasHandledInitialUrl(true);
        } else {
          // If deal doesn't exist in current, we'll check archived after it loads
          console.log("Deal not found in current, waiting for archived data");
        }
      }

      // UI is now ready with current deals - set loading to false
      setIsLoading(false);

      // Load archived data in the background (non-blocking)
      (async () => {
        try {
          const archivedData = await fetchDealsData("archived");

          // Process archived deals data
          if (
            Object.keys(archivedData.allInOne).length === 0 ||
            Object.keys(archivedData.facilities).length === 0
          ) {
            console.warn("⚠️ No archived deals data available");
            setArchivedDealsData({});
            setHasArchivedDealsData(false);

            if (viewingArchived) {
              console.warn(
                "Falling back to current data since archived data is not available"
              );
              setViewingArchived(false);
              setConsolidatedData(currentConsolidated);
              setCompetitionData(currentCompetitionData);
            }
          } else {
            // Process and normalize the archived data
            const {
              inconsistencies: archivedInconsistencies,
              dealMap: archivedDealMap,
            } = validateAndNormalizeData(
              archivedData.allInOne,
              archivedData.facilities,
              archivedData.hudDebt
            );

            // Create consolidated archived data
            const archivedConsolidated = createConsolidatedData(
              archivedData.allInOne,
              archivedData.facilities,
              archivedData.hudDebt,
              archivedDealMap,
              archivedInconsistencies
            );

            setArchivedDealsData(archivedConsolidated);
            setHasArchivedDealsData(true);
            // Set archived competition data
            setArchivedCompetitionData(archivedData.competitionOverview);

            // If viewing archived, update the display data now
            if (viewingArchived) {
              setConsolidatedData(archivedConsolidated);
              setCompetitionData(archivedData.competitionOverview);
            }

            // Handle URL-based deal selection for archived deals if needed
            if (urlDealName && !hasHandledInitialUrl && viewingArchived) {
              if (archivedConsolidated && archivedConsolidated[urlDealName]) {
                const deal = archivedConsolidated[urlDealName];
                setCurrentDeal(deal);
                setCurrentDealName(urlDealName);
                setCurrentGroup(null);

                const consolidatedFacility = deal.facilities["Consolidated"];
                if (consolidatedFacility) {
                  setCurrentFacility(consolidatedFacility);
                  const periods = Object.keys(
                    consolidatedFacility.periods || {}
                  );
                  const defaultPeriod = findDefaultPeriod(periods);
                  if (defaultPeriod) {
                    setSelectedPeriod(defaultPeriod);
                  }
                }
                setHasHandledInitialUrl(true);
              } else {
                // Deal not found in archived either
                navigate("/deals");
                setHasHandledInitialUrl(true);
              }
            }
          }

          console.log("✅ Archived deals loaded successfully in background");
        } catch (error) {
          console.error("Error loading archived data:", error);
          setArchivedDealsData({});
          setHasArchivedDealsData(false);
        }
      })();
    } catch (error) {
      console.error("Error in fetchData:", error);
      setIsLoading(false);
      alert("An error occurred while loading data. Please try again later.");
    }
  };

  // Helper function to fetch deal data from a specific folder
  const fetchDealsData = async (folder) => {
    try {
      // Let's explicitly track what structure we're trying
      let allInOneUrl,
        facilitiesUrl,
        hudDebtUrl,
        portfolioFacilitiesUrl,
        competitionOverviewUrl;

      if (folder === "current") {
        allInOneUrl = await getUrl({
          path: `protected/current/AllInOneJson.json`,
        });
        facilitiesUrl = await getUrl({
          path: `protected/current/AllFacilities.json`,
        });
        hudDebtUrl = await getUrl({
          path: `protected/current/HudDebtJson.json`,
        });
        portfolioFacilitiesUrl = await getUrl({
          path: `protected/portfolioFacilities.json`,
        });
        competitionOverviewUrl = await getUrl({
          path: "protected/current/CompetitionOverview.json",
        });
      } else {
        // For archived, use protected/archived/ folder
        allInOneUrl = await getUrl({
          path: `protected/archived/AllInOneJson.json`,
        });
        facilitiesUrl = await getUrl({
          path: `protected/archived/AllFacilities.json`,
        });
        hudDebtUrl = await getUrl({
          path: `protected/archived/HudDebtJson.json`,
        });
        portfolioFacilitiesUrl = await getUrl({
          path: `protected/portfolioFacilities.json`,
        });
        competitionOverviewUrl = await getUrl({
          path: "protected/archived/CompetitionOverview.json",
        });
      }

      // Helper function to fetch and handle errors
      const fetchWithErrorHandling = async (url, name) => {
        try {
          const response = await fetch(url.url.href);

          if (!response.ok) {
            const errorStatus = response.status;
            console.error(
              `Error fetching ${name} (${errorStatus}):`,
              url.url.href
            );

            return {};
          }

          const data = await response.json();
          return data;
        } catch (error) {
          console.error(`Failed to fetch ${name}:`, error);
          return {};
        }
      };

      // Fetch all data with improved error handling
      const allInOne = await fetchWithErrorHandling(allInOneUrl, "allInOne");
      const facilities = await fetchWithErrorHandling(
        facilitiesUrl,
        "facilities"
      );
      const hudDebt = await fetchWithErrorHandling(hudDebtUrl, "hudDebt");
      const portfolioFacilities = await fetchWithErrorHandling(
        portfolioFacilitiesUrl,
        "portfolioFacilities"
      );
      const competitionOverview = await fetchWithErrorHandling(
        competitionOverviewUrl,
        "competitionOverview"
      );

      return {
        allInOne,
        facilities,
        hudDebt,
        portfolioFacilities,
        competitionOverview,
      };
    } catch (error) {
      console.error(`Error in fetchDealsData for ${folder}:`, error);
      return {
        allInOne: {},
        facilities: {},
        hudDebt: {},
        portfolioFacilities: {},
        competitionOverview: {},
      };
    }
  };

  // Function to toggle between current and archived views
  const toggleArchiveView = async () => {
    // If we're loading data, don't allow toggling
    if (isLoading) {
      console.log("Data is currently loading, ignoring toggle request");
      return;
    }

    // Additional permission check as a safeguard
    if (!hasArchiveAccess) {
      console.error(
        "Permission denied: User doesn't have access to archive functionality"
      );
      return; // Exit without toggling
    }

    if (viewingArchived) {
      // Check if current data is available
      if (!hasCurrentDealsData) {
        console.warn("Current deals data is not available");
        alert("Current deals data is not available. Please try again later.");
        return;
      }

      const previousFacility = currentFacility;
      const previousPeriod = selectedPeriod;

      // Clear selections
      setCurrentGroup(null);
      setCurrentDeal(null);
      setCurrentDealName(null);
      setCurrentFacility(null);

      // Switch to current view
      setConsolidatedData(currentDealsData);
      setCompetitionData(currentCompetitionData);
      setViewingArchived(false);

      // Restore the selected period if possible
      if (previousPeriod) {
        setSelectedPeriod(previousPeriod);
      }
    } else {
      // Switching from current to archived view
      console.log("Switching to archived view");

      // Check if archived data is available
      if (!hasArchivedDealsData) {
        console.warn("Archived deals data is not available");
        alert("Archived deals data is not available. Please check back later.");
        return;
      }

      const previousFacility = currentFacility;
      const previousPeriod = selectedPeriod;

      setCurrentGroup(null);
      setCurrentDeal(null);
      setCurrentDealName(null);
      setCurrentFacility(null);
      setConsolidatedData(archivedDealsData);
      setCompetitionData(archivedCompetitionData);
      setViewingArchived(true);

      // Restore the selected period if possible
      if (previousPeriod) {
        setSelectedPeriod(previousPeriod);
      }
    }
    // Navigate back to the deals list
    navigate("/deals");
  };

  // Move selectDeal function before the useEffect that uses it
  const selectDeal = async (dealName, previousPeriod = null) => {
    setIsDealLoading(true);

    try {
      if (!dealName) {
        // Clear selection
        setCurrentDeal(null);
        setCurrentDealName(null);
        setCurrentFacility(null);
        setSelectedPeriod(null);
        setCurrentGroup(null);

        // Update URL without replace to maintain history
        if (window.location.pathname !== "/deals") {
          navigate("/deals");
        }
        return;
      }

      if (consolidatedData && consolidatedData[dealName]) {
        const deal = consolidatedData[dealName];

        // Set all states in one go to avoid race conditions
        setCurrentDeal(deal);
        setCurrentDealName(dealName);
        setCurrentGroup(null);

        // Handle consolidated facility selection
        const consolidatedFacility = deal.facilities["Consolidated"];
        if (consolidatedFacility) {
          setCurrentFacility(consolidatedFacility);

          // Check if previous period exists in new facility
          const periods = Object.keys(consolidatedFacility.periods || {});

          // Ensure we always set a period - this addresses the issue
          if (periods.length > 0) {
            if (previousPeriod) {
              // Try to find a period with the same first 3 characters (T1, T2, T3, etc.)
              const previousPrefix = previousPeriod.substring(0, 3);
              const matchingPeriod = periods.find(
                (p) => p.substring(0, 3) === previousPrefix
              );

              if (matchingPeriod) {
                // Keep the period with the same prefix if available
                setSelectedPeriod(matchingPeriod);
              } else {
                // If no matching prefix, default to T3 or closest trailing period
                const defaultPeriod = findDefaultPeriod(periods);
                setSelectedPeriod(defaultPeriod);
              }
            } else {
              // Otherwise set default period (prefer T3 or closest trailing period)
              const defaultPeriod = findDefaultPeriod(periods);
              setSelectedPeriod(defaultPeriod);
            }
          } else {
            // No periods available, reset selectedPeriod
            setSelectedPeriod(null);
            console.warn(`No periods available for deal: ${dealName}`);
          }
        } else {
          // No consolidated facility, reset both facility and period
          setCurrentFacility(null);
          setSelectedPeriod(null);
          console.warn(`No consolidated facility for deal: ${dealName}`);
        }

        // Update URL without replace to maintain history
        const currentPath = window.location.pathname;
        const expectedPath = `/deals/${encodeURIComponent(dealName)}`;
        if (currentPath !== expectedPath) {
          navigate(expectedPath);
        }
      }
    } finally {
      setIsDealLoading(false);
    }
  };

  // Effect to handle initial data load and URL
  useEffect(() => {
    if (!consolidatedData || !isAuthenticated || hasHandledInitialUrl) return;

    const path = window.location.pathname;
    const match = path.match(/\/deals\/([^/]+)/);

    if (match && match[1]) {
      const dealNameFromUrl = decodeURIComponent(match[1]);

      if (consolidatedData[dealNameFromUrl]) {
        // Set the flag to prevent duplicate loading
        setHasHandledInitialUrl(true);
        selectDeal(dealNameFromUrl);
      } else {
        setHasHandledInitialUrl(true);
        navigate("/deals");
      }
    } else {
      // Mark as handled even if no deal in URL
      setHasHandledInitialUrl(true);
    }
  }, [consolidatedData, isAuthenticated, hasHandledInitialUrl]);

  // Effect to handle URL changes
  useEffect(() => {
    if (!consolidatedData || !isAuthenticated) return;

    const handleUrlChange = () => {
      const path = window.location.pathname;
      const match = path.match(/\/deals\/([^/]+)/);

      if (match && match[1]) {
        const dealNameFromUrl = decodeURIComponent(match[1]);

        if (dealNameFromUrl !== currentDealName) {
          if (consolidatedData[dealNameFromUrl]) {
            selectDeal(dealNameFromUrl);
          } else {
            navigate("/deals");
          }
        }
      } else if (path === "/deals" && currentDealName) {
        setCurrentDeal(null);
        setCurrentDealName(null);
        setCurrentFacility(null);
        setSelectedPeriod(null);
        setCurrentGroup(null);
      }
    };

    // Listen for history changes (back/forward buttons)
    window.addEventListener("popstate", handleUrlChange);

    return () => {
      window.removeEventListener("popstate", handleUrlChange);
    };
  }, [consolidatedData, currentDealName, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchFacilityGroups();
    }
  }, [isAuthenticated]);

  const fetchFacilityGroups = async () => {
    if (!isAuthenticated) return;

    try {
      setIsLoading(true);

      // Fetch groups data from S3
      try {
        const groupsUrl = await getUrl({
          path: "public/facilityGroups.json",
        });

        const response = await fetch(groupsUrl.url.href);

        if (response.ok) {
          const groupsData = await response.json();

          // Store ALL groups - this is important for preserving private groups
          setAllFacilityGroups(groupsData);

          // Filter groups based on privacy settings for display only
          const currentUser = await getCurrentUser();
          const filteredGroups = groupsData.filter(
            (group) =>
              !group.isPrivate || group.createdBy === currentUser.username
          );
          setFacilityGroups(filteredGroups);
        } else if (response.status !== 404) {
          // Error fetching groups
        }
      } catch (error) {
        setFacilityGroups([]);
        setAllFacilityGroups([]);
      }
    } catch (error) {
      // Error in fetchFacilityGroups
    } finally {
      setIsLoading(false);
    }
  };

  // Add function to create a new group
  const createGroup = async (groupName, facilityIds, isPrivate) => {
    try {
      if (!currentDealName || !groupName || facilityIds.length === 0) {
        return false;
      }

      const currentUser = await getCurrentUser();
      const newGroup = {
        id: `group-${Date.now()}`, // This already includes the prefix, which is correct
        name: groupName,
        dealId: currentDealName,
        facilityIds,
        isPrivate,
        createdBy: currentUser.username,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Update the filtered groups for display
      const updatedFilteredGroups = [...facilityGroups, newGroup];
      setFacilityGroups(updatedFilteredGroups);

      // Update the complete list of ALL groups
      const updatedAllGroups = [...allFacilityGroups, newGroup];
      setAllFacilityGroups(updatedAllGroups);

      // Save the COMPLETE list of groups to S3
      await saveGroupsToS3(updatedAllGroups);

      return true;
    } catch (error) {
      // Error creating group
      return false;
    }
  };

  // Add function to delete a group
  const deleteGroup = async (groupId) => {
    try {
      // First check in the filtered list that's visible to the user
      const groupToDelete = facilityGroups.find(
        (group) => group.id === groupId
      );
      if (!groupToDelete) {
        // Group not found
        return false;
      }

      // Check if current user is the creator
      const currentUser = await getCurrentUser();
      if (groupToDelete.createdBy !== currentUser.username) {
        // Only the creator can delete this group
        return false;
      }

      // Update both the filtered list and complete list
      const updatedFilteredGroups = facilityGroups.filter(
        (group) => group.id !== groupId
      );
      setFacilityGroups(updatedFilteredGroups);

      const updatedAllGroups = allFacilityGroups.filter(
        (group) => group.id !== groupId
      );
      setAllFacilityGroups(updatedAllGroups);

      // If current group is being deleted, clear it
      if (currentGroup?.id === groupId) {
        setCurrentGroup(null);
      }

      // Save the COMPLETE list of groups to S3
      await saveGroupsToS3(updatedAllGroups);

      return true;
    } catch (error) {
      // Error deleting group
      return false;
    }
  };

  // Add function to update a group
  const updateGroup = async (groupId, updates) => {
    try {
      // Update in the filtered list
      const updatedFilteredGroups = facilityGroups.map((group) =>
        group.id === groupId
          ? { ...group, ...updates, updatedAt: new Date().toISOString() }
          : group
      );
      setFacilityGroups(updatedFilteredGroups);

      // Update in the complete list
      const updatedAllGroups = allFacilityGroups.map((group) =>
        group.id === groupId
          ? { ...group, ...updates, updatedAt: new Date().toISOString() }
          : group
      );
      setAllFacilityGroups(updatedAllGroups);

      // If current group is updated, update that reference too
      if (currentGroup?.id === groupId) {
        const updatedGroup = updatedAllGroups.find((g) => g.id === groupId);
        setCurrentGroup(updatedGroup);
      }

      // Save the COMPLETE list to S3
      await saveGroupsToS3(updatedAllGroups);

      return true;
    } catch (error) {
      // Error updating group
      return false;
    }
  };

  // Helper function to save groups to S3
  const saveGroupsToS3 = async (groups) => {
    const blob = new Blob([JSON.stringify(groups)], {
      type: "application/json",
    });

    try {
      await uploadData({
        path: "public/facilityGroups.json",
        data: blob,
      }).result;
    } catch (error) {
      // Silent fail - error handling is done at the caller level
    }
  };

  // Add function to select a group
  const selectGroup = async (groupId, previousPeriod = null) => {
    setIsDealLoading(true);

    try {
      // Ensure groupId has the prefix for comparison
      if (typeof groupId === "string" && !groupId.startsWith("group-")) {
        groupId = `group-${groupId}`;
      }

      const group = facilityGroups.find((g) => g.id === groupId);

      if (!group) {
        setCurrentGroup(null);
        return;
      }

      // Get deal data directly
      const dealData = consolidatedData[group.dealId];

      if (!dealData) {
        console.log(`[DEBUG] Deal data not found for dealId: ${group.dealId}`);
        return;
      }

      // First update the deal context since group data depends on it
      setCurrentDeal(dealData);
      setCurrentDealName(group.dealId);

      const groupData = calculateGroupData(group, dealData);

      if (!groupData) {
        console.log(`[DEBUG] Group data calculation failed`);
        return;
      }

      // Force isGroup flag and groupId
      if (groupData.details) {
        groupData.details.isGroup = true;
        groupData.details.groupId = group.id;
        groupData.details.Facilities = `Group: ${group.name}`; // Ensure the group name is set
      }

      // Add a specific flag to ensure UI components recognize this as group data
      groupData._isGroupData = true;

      // Update state in sequence
      setCurrentGroup(group);

      setCurrentFacility(groupData);

      // Set period
      const periods = Object.keys(groupData.periods || {});
      if (periods.length > 0) {
        let selectedPeriodValue;
        if (previousPeriod && periods.includes(previousPeriod)) {
          selectedPeriodValue = previousPeriod;
        } else {
          selectedPeriodValue = findDefaultPeriod(periods);
        }
        setSelectedPeriod(selectedPeriodValue);
      }

      // Update URL to reflect both deal and group
      const currentPath = window.location.pathname;
      const expectedPath = `/deals/${encodeURIComponent(group.dealId)}`;
      if (currentPath !== expectedPath) {
        navigate(expectedPath);
      }
    } catch (error) {
      console.error("[ERROR] Error in selectGroup:", error);
      // Reset states on error
      setCurrentGroup(null);
      setCurrentFacility(null);
    } finally {
      setIsDealLoading(false);
    }
  };

  const selectFacility = (facilityName, previousPeriod = null) => {
    if (!currentDeal || !currentDeal.facilities[facilityName]) {
      setCurrentFacility("Consolidated");
      setSelectedPeriod(null);
      return;
    }

    const facility = currentDeal.facilities[facilityName];
    setCurrentFacility(facility);

    // Check if previous period exists in new facility
    const periods = Object.keys(facility.periods || {});

    if (previousPeriod) {
      // Try to find a period with the same first 3 characters (T1, T2, T3, etc.)
      const previousPrefix = previousPeriod.substring(0, 3);
      const matchingPeriod = periods.find(
        (p) => p.substring(0, 3) === previousPrefix
      );

      if (matchingPeriod) {
        // Keep the period with the same prefix if available
        setSelectedPeriod(matchingPeriod);
        return;
      }
    }

    // Otherwise set default period (prefer T3 or closest trailing period)
    const defaultPeriod = findDefaultPeriod(periods);
    if (defaultPeriod) {
      setSelectedPeriod(defaultPeriod);
    }
  };

  // Fetch data from S3
  useEffect(() => {
    if (!isAuthenticated) return;

    // Ensure we have a valid session before proceeding
    const checkSessionAndFetch = async () => {
      try {
        const session = await fetchAuthSession();
        if (!session?.tokens?.accessToken?.toString()) {
          console.error("No valid session found");
          setIsAuthenticated(false);
          return;
        }

        // Proceed with data fetching
        fetchData();
      } catch (error) {
        console.error("Error checking auth session:", error);
        if (error.name === "UserUnAuthenticatedException") {
          setIsAuthenticated(false);
        }
      }
    };

    checkSessionAndFetch();
  }, [isAuthenticated]);

  // Handle URL-based deal selection when data or URL changes
  useEffect(() => {
    if (
      !urlDealName ||
      isLoading ||
      !currentDealsData ||
      (!hasArchivedDealsData && !archivedDealsData)
    )
      return;

    // If we have a URL for a deal, check both current and archived deals
    const currentDeal = currentDealsData[urlDealName];
    const archivedDeal =
      archivedDealsData && Object.keys(archivedDealsData).length > 0
        ? archivedDealsData[urlDealName]
        : null;

    if (currentDeal) {
      // Deal exists in current deals
      setCurrentDeal(currentDeal);
      setCurrentDealName(urlDealName);
      setCurrentGroup(null);
      setConsolidatedData(currentDealsData);
      setCompetitionData(currentCompetitionData);
      setViewingArchived(false);

      // Handle consolidated facility selection
      const consolidatedFacility = currentDeal.facilities["Consolidated"];
      if (consolidatedFacility) {
        setCurrentFacility(consolidatedFacility);
        const periods = Object.keys(consolidatedFacility.periods || {});
        const defaultPeriod = findDefaultPeriod(periods);
        if (defaultPeriod) {
          setSelectedPeriod(defaultPeriod);
        }
      }
    } else if (archivedDeal && hasArchivedDealsData) {
      // Deal exists in archived deals
      setCurrentDeal(archivedDeal);
      setCurrentDealName(urlDealName);
      setCurrentGroup(null);
      setConsolidatedData(archivedDealsData);
      setCompetitionData(archivedCompetitionData);
      setViewingArchived(true);

      // Handle consolidated facility selection
      const consolidatedFacility = archivedDeal.facilities["Consolidated"];
      if (consolidatedFacility) {
        setCurrentFacility(consolidatedFacility);
        const periods = Object.keys(consolidatedFacility.periods || {});
        const defaultPeriod = findDefaultPeriod(periods);
        if (defaultPeriod) {
          setSelectedPeriod(defaultPeriod);
        }
      }
    } else if (urlDealName) {
      // If deal doesn't exist in either current or archived data, redirect to deals list
      console.warn(`Deal ${urlDealName} not found in current or archived data`);
      navigate("/deals");
    }
  }, [
    currentDealsData,
    archivedDealsData,
    hasArchivedDealsData,
    isLoading,
    urlDealName,
    navigate,
  ]);

  // Update session storage when archive state changes
  useEffect(() => {
    sessionStorage.setItem("viewingArchived", viewingArchived.toString());
  }, [viewingArchived]);

  return (
    <DealContext.Provider
      value={{
        groups: facilityGroups,
        facilityGroups,
        consolidatedData,
        dealNames: consolidatedData ? Object.keys(consolidatedData) : [],
        currentGroup,
        currentDeal,
        currentDealName,
        currentFacility,
        selectedPeriod,
        portfolioFacilities,
        dataInconsistencies,
        viewingArchived,
        isLoading,
        hasCurrentDealsData,
        hasArchivedDealsData,
        hasArchiveAccess,
        setCurrentGroup,
        setCurrentDeal,
        setCurrentDealName,
        setCurrentFacility,
        setSelectedPeriod,
        toggleArchiveView,
        selectDeal,
        selectFacility,
        selectGroup,
        isDealLoading,
        createGroup,
        deleteGroup,
        updateGroup,
        competitionData,
      }}
    >
      {children}
    </DealContext.Provider>
  );
};

export const useDealData = () => useContext(DealContext);
